/**
 * The activity graph's view state (D240): the filters, the search, the time
 * scrubber's window and the selection, over one graph. Portable like
 * ActivityGraph.tsx: React and the DOM-free model only.
 */
import { useMemo, useState } from 'react';

import type { ActivityGraph, ActivityNode } from '@aioe/shared';
import { ALL_GROUPS, type FilterGroupId, type GraphLayout, layoutActivityGraph, matchesSearch, timeSpan, wholeWindow, windowTimes } from './activityGraphModel.ts';

/** The view's state: filters, search, the time window, the selection. */
export interface ActivityGraphView {
  graph: ActivityGraph;
  layout: GraphLayout;
  groups: ReadonlySet<FilterGroupId>;
  toggleGroup(id: FilterGroupId): void;
  query: string;
  setQuery(q: string): void;
  matches: ActivityNode[];
  /** The scrubber's two handles, 0–1 along the graph's time span. */
  range: [number, number];
  setRange(r: [number, number]): void;
  span: { from: number; to: number };
  window?: { from: number; to: number };
  selected?: ActivityNode;
  select(id: string | undefined): void;
  focused?: string;
  setFocused(id: string | undefined): void;
}

export function useActivityGraphView(graph: ActivityGraph): ActivityGraphView {
  const [groups, setGroups] = useState<ReadonlySet<FilterGroupId>>(ALL_GROUPS);
  const [query, setQuery] = useState('');
  const [range, setRange] = useState<[number, number]>([0, 1]);
  const [selectedId, select] = useState<string | undefined>();
  const [focused, setFocused] = useState<string | undefined>();
  const layout = useMemo(() => layoutActivityGraph(graph, groups), [graph, groups]);
  const span = useMemo(() => timeSpan(graph), [graph]);
  const window = wholeWindow(range[0], range[1]) ? undefined : windowTimes(span, range[0], range[1]);
  const matches = useMemo(() => (query.trim() ? layout.nodes.map((p) => p.node).filter((n) => matchesSearch(n, query)) : []), [layout, query]);
  const selected = selectedId ? layout.nodes.find((p) => p.node.id === selectedId)?.node : undefined;
  return {
    graph,
    layout,
    groups,
    toggleGroup: (id) =>
      setGroups((g) => {
        const next = new Set(g);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    query,
    setQuery,
    matches,
    range,
    setRange,
    span,
    ...(window ? { window } : {}),
    ...(selected ? { selected } : {}),
    select,
    ...(focused ? { focused } : {}),
    setFocused,
  };
}

